import { searchFilterDebounceMs } from '@vs-shop/engine/config/glossary';
import { RuntimeClientAtom } from '@vs-shop/engine/runtime/RuntimeClient';
import { Duration, Effect, Match, Option } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';

import {
  alphabetFilterSearchParamAtom,
  currentNameFilterAtom,
  nameFilterSearchParamAtom,
} from './glossaryFilters';

/**
 * What the search input shows, and on whose authority.
 *
 * `Typed` is the user holding the pen: their text stands until the debounced
 * write lands, trailing spaces and all. `Applied` records the `?name=` value
 * written alongside the text, so a URL carrying a different one is known to
 * have come from somewhere else — a Back, a Forward, a pasted link — and the
 * input follows it instead.
 */
type SearchDraft =
  | { readonly _tag: 'Typed'; readonly value: string }
  | { readonly _tag: 'Applied'; readonly value: string; readonly nameFilter: string | null };

const searchDraftStateAtom = Atom.make<SearchDraft>({
  _tag: 'Applied',
  value: '',
  nameFilter: null,
});

/**
 * The search input's value.
 *
 * Derived rather than stored, because the URL has a second writer: the
 * `popstate` listener inside `Atom.searchParam` sets the filter atoms directly
 * on Back/Forward, never reaching `glossaryFilterActionFn`. A plain stored
 * draft therefore survives a history move that changed the results underneath
 * it, leaving the box empty over a list of search hits — and the clear button
 * hides itself on an empty value, so there is no way out.
 */
export const glossarySearchDraftAtom = Atom.readable<string>((ctx) => {
  const nameFilter = ctx.get(currentNameFilterAtom);

  return Match.value(ctx.get(searchDraftStateAtom)).pipe(
    Match.tag('Typed', (draft) => draft.value),
    Match.tag('Applied', (draft) =>
      draft.nameFilter === nameFilter ? draft.value : (nameFilter ?? '')
    ),
    Match.exhaustive
  );
});

export type GlossaryFilterAction =
  | { readonly _tag: 'Type'; readonly value: string }
  | { readonly _tag: 'SelectLetter'; readonly letter: string }
  | { readonly _tag: 'Clear' };

/**
 * The single writer for the glossary filter state: the search draft and both
 * search params always move together and in a fixed order. A caller that wrote
 * two of them itself would race, because each write schedules its own URL
 * update.
 *
 * `Atom.fn` is non-concurrent, so a new action interrupts the one in flight —
 * that is what debounces `Type` while leaving `Clear` and `SelectLetter`
 * immediate. The name and alphabet filters are mutually exclusive: setting
 * either clears the other.
 */
export const glossaryFilterActionFn = RuntimeClientAtom.fn(
  Effect.fnUntraced(function* (action: GlossaryFilterAction, ctx: Atom.FnContext) {
    const applyFilters = (
      draft: string,
      name: Option.Option<string>,
      letter: Option.Option<string>
    ) =>
      Effect.sync(() => {
        ctx.set(searchDraftStateAtom, {
          _tag: 'Applied',
          value: draft,
          nameFilter: Option.getOrElse(name, () => null),
        });
        ctx.set(nameFilterSearchParamAtom, name);
        ctx.set(alphabetFilterSearchParamAtom, letter);
      });

    yield* Match.value(action).pipe(
      Match.tag('Clear', () => applyFilters('', Option.none(), Option.none())),
      Match.tag('SelectLetter', ({ letter }) =>
        applyFilters('', Option.none(), Option.some(letter))
      ),
      Match.tag('Type', ({ value }) =>
        Effect.gen(function* () {
          ctx.set(searchDraftStateAtom, { _tag: 'Typed', value });
          yield* Effect.sleep(Duration.millis(searchFilterDebounceMs));

          const trimmed = value.trim();
          yield* applyFilters(
            value,
            trimmed.length > 0 ? Option.some(trimmed) : Option.none(),
            Option.none()
          );
        })
      ),
      Match.exhaustive
    );
  })
);
